import React, { FC, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Box, Icon, Typography, useTheme } from '@mui/material';
import CommonModal from '@/components/customModal/commonModal/commonModal';
import { getSectionHelp } from './sectionHelpResolver';
import { IHelpKitModalProps } from './interface/helpkit.interface';
import { useHelpKitModalStyle } from './helpkitModal.style';

const renderTextWithHashtags = (text: string, color: string): ReactNode[] => {
  const parts = text.split(/(\#\w+)/g).filter((part) => part.length > 0);
  return parts.map((part, i) =>
    part.startsWith('#')
      ? <span key={i} style={{ color }}>{part}</span>
      : part
  );
};

const HelpKitModal: FC<IHelpKitModalProps> = ({
  open,
  onClose,
  sectionKey,
}) => {
  const theme = useTheme();
  const classes = useHelpKitModalStyle(theme);
  const info = getSectionHelp(sectionKey);

  if (!info) {
    return null;
  }

  const markdownComponents = {
    p: ({ children }: { children: React.ReactNode[] }) => (
      <p>
        {React.Children.toArray(children).map((child, i) =>
          typeof child === 'string'
            ? renderTextWithHashtags(child, theme.palette.secondary.main).map((part, j) =>
                <React.Fragment key={`${i}-${j}`}>{part}</React.Fragment>
              )
            : child
        )}
      </p>
    ),
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      maxWidth="md"
      disableScrollLock
      title={
        <Box sx={classes.modalTitle}>
          <Box sx={classes.modalIconBox}>
            <Icon sx={classes.modalIcon}>{info.icon}</Icon>
          </Box>
          <Typography
            component="span"
            variant="h6"
            fontWeight={700}
            sx={{ color: theme.palette.custom.gray900 }}
          >
            {info.title}
          </Typography>
        </Box>
      }
    >
      <Box sx={classes.modalContent}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          components={markdownComponents}
        >
          {info.description}
        </ReactMarkdown>

        {info.bullets.length > 0 && (
          <Box component="ul" sx={classes.bulletList}>
            {info.bullets.map((point, i) => (
              <Box key={i} component="li" sx={classes.bulletItem}>
                <Typography variant="body2" sx={classes.bulletText}>
                  {renderTextWithHashtags(point, theme.palette.secondary.main)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </CommonModal>
  );
};

export default HelpKitModal;
